import { useEffect, useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useSearchParams } from 'react-router-dom'
import DateTimePicker, {
  formatDateLabel,
  formatTimeLabel,
  type DateTimeValue,
} from './DateTimePicker'
import Passenger from './Passenger'
import DayTripDropdown from './DayTripDropdown'
import HeroLocationInput from './HeroLocationInput'
import {
  type LocationGeoData,
  isLocationInSalzburg,
} from '../utils/salzburgLocation'

import {
  HOURLY_DURATION_OPTIONS,
  getHourlyOption,
  type HourlyDurationOption,
} from '../constants/hourlyBooking'

type LocationValue = LocationGeoData | null

type SearchFormProps = {
  serviceType?: 'transfers' | 'hourly' | 'daytrips'
}

function FieldChevron({ open = false }: { open?: boolean }) {
  return (
    <svg
      width="16"
      height="10"
      viewBox="0 0 16 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`h-[clamp(7px,0.8vw,10px)] w-[clamp(11px,1.25vw,16px)] shrink-0 transition-transform duration-200 ${
        open ? 'rotate-180' : ''
      }`}
      aria-hidden
    >
      <path
        d="M1 1.5L8 8.5L15 1.5"
        stroke="black"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function SearchForm({ serviceType = 'transfers' }: SearchFormProps) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const isHourly = serviceType === 'hourly'

  const blockBase =
    'relative min-w-0 h-[102px] bg-white px-[20px] flex flex-col justify-center overflow-visible border-b border-[#ECEFF1] rounded-none md:h-[clamp(68px,12.2vh,96px)] md:rounded-[clamp(10px,1.3vw,14px)] md:border-0 md:px-[clamp(14px,1.8vw,26px)]'

  const labelClass =
    'font-poppins text-[11px] font-medium uppercase leading-none tracking-[0.04em] text-[#9AA3B7] md:text-[clamp(7px,0.85vw,12px)] md:normal-case md:tracking-[-0.03em] md:text-black'

  const valueClass =
    'font-poppins text-[11px] font-bold leading-[1.05] tracking-[-0.035em] text-black md:text-[17px]'

  const subClass =
    'font-poppins text-[13px] font-normal leading-[1.25] tracking-[-0.02em] text-[#9AA3B7] md:text-[clamp(6px,0.72vw,10px)] md:font-medium md:tracking-[-0.03em] md:text-black'

  const placeholderValueClass =
    'font-poppins text-[18px] font-normal leading-[1.05] tracking-[-0.035em] text-[#A0A0A0] md:text-[20px]'

  const [isDurationOpen, setIsDurationOpen] = useState(false)
  const [isDepartureOpen, setIsDepartureOpen] = useState(false)
  const [isReturnOpen, setIsReturnOpen] = useState(false)
  const [isPassengerOpen, setIsPassengerOpen] = useState(false)
  const [isDayTripOpen, setIsDayTripOpen] = useState(false)

  const [isLargeGroupModalOpen, setIsLargeGroupModalOpen] = useState(false)
  const [isQuoteSent, setIsQuoteSent] = useState(false)
  const [quoteTravelDate, setQuoteTravelDate] = useState('')
  const [quoteCategory, setQuoteCategory] = useState('Transfers')
  const [isQuoteCategoryOpen, setIsQuoteCategoryOpen] = useState(false)
  const [quotePickup, setQuotePickup] = useState('')
  const [quoteDropoff, setQuoteDropoff] = useState('')
  const [quoteRequirements, setQuoteRequirements] = useState('')

  const [selectedFrom, setSelectedFrom] = useState<LocationValue>(null)
  const [selectedTo, setSelectedTo] = useState<LocationValue>(null)
  const [lockedSkiTransfer, setLockedSkiTransfer] = useState<{
    id: string
    title: string
    address: string
    price?: number
  } | null>(null)
  const [durationHours, setDurationHours] = useState<number | null>(null)
  const [selectedDurationOption, setSelectedDurationOption] =
    useState<HourlyDurationOption | null>(null)

  const [adults, setAdults] = useState(2)
  const [children, setChildren] = useState(0)

  const [departure, setDeparture] = useState<DateTimeValue | null>(null)
  const [returnTrip, setReturnTrip] = useState<DateTimeValue | null>(null)

  const fromFieldRef = useRef<HTMLDivElement>(null)
  const toFieldRef = useRef<HTMLDivElement>(null)
  const durationFieldRef = useRef<HTMLDivElement>(null)
  const departureFieldRef = useRef<HTMLDivElement>(null)
  const returnFieldRef = useRef<HTMLDivElement>(null)
  const passengerFieldRef = useRef<HTMLDivElement>(null)
  const dayTripFieldRef = useRef<HTMLDivElement>(null)

  const activeDurationOption =
    selectedDurationOption ||
    (durationHours ? getHourlyOption(durationHours) : null)

  const showReturn =
    !isHourly && Boolean(selectedFrom && selectedTo && departure)

  // Mutual Salzburg base logic:
  // At least one of FROM or TO must be inside Salzburg.
  // If FROM is outside Salzburg, TO must be suggested strictly inside Salzburg only.
  // If TO is outside Salzburg, FROM must be suggested strictly inside Salzburg only.
  const isFromOutsideSalzburg = Boolean(selectedFrom && selectedFrom.isSalzburg === false)
  const isToOutsideSalzburg = Boolean(selectedTo && selectedTo.isSalzburg === false)

  const isToRestrictedToSalzburg = isFromOutsideSalzburg
  const isFromRestrictedToSalzburg = isHourly ? true : isToOutsideSalzburg

  const isAtLeastOneInSalzburg = isHourly
    ? Boolean(selectedFrom?.isSalzburg)
    : Boolean(selectedFrom?.isSalzburg || selectedTo?.isSalzburg)

  const isSearchReady = isHourly
    ? Boolean(selectedFrom && (durationHours || selectedDurationOption) && departure)
    : Boolean(selectedFrom && selectedTo && departure && isAtLeastOneInSalzburg)

  const closeAllDropdowns = () => {
    setIsDurationOpen(false)
    setIsDepartureOpen(false)
    setIsReturnOpen(false)
    setIsPassengerOpen(false)
    setIsDayTripOpen(false)
  }

  useEffect(() => {
    closeAllDropdowns()
  }, [serviceType])

  useEffect(() => {
    if (serviceType !== 'transfers' && lockedSkiTransfer) {
      setLockedSkiTransfer(null)
      setSelectedTo(null)
    }
  }, [serviceType])

  // Synchronize with URL search parameters for ski transfers
  useEffect(() => {
    const st = searchParams.get('serviceType')
    const toTitle = searchParams.get('toTitle')
    const serviceId = searchParams.get('serviceId')
    const servicePrice = searchParams.get('servicePrice')

    if (st === 'skitransfers' && toTitle) {
      const toAddr = searchParams.get('toAddress') || `${toTitle}, Austria`
      setSelectedTo({
        title: toTitle,
        address: toAddr,
        isSalzburg: false,
      })
      setLockedSkiTransfer({
        id: serviceId || '',
        title: toTitle,
        address: toAddr,
        price: servicePrice ? Number(servicePrice) : undefined,
      })

      // Smooth scroll to hero transfers search
      setTimeout(() => {
        const transfersEl = document.getElementById('transfers')
        if (transfersEl) {
          transfersEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 80)
    }
  }, [searchParams])

  // Also listen for in-page ski destination selection events
  useEffect(() => {
    const handleCustomSki = (e: Event) => {
      const customEvent = e as CustomEvent<{
        id: string
        title: string
        address?: string
        price?: number
      }>
      if (customEvent.detail && customEvent.detail.title) {
        const { id, title, address, price } = customEvent.detail
        const toAddr = address || `${title}, Austria`
        setSelectedTo({
          title,
          address: toAddr,
          isSalzburg: false,
        })
        setLockedSkiTransfer({
          id: id || '',
          title,
          address: toAddr,
          price,
        })

        setTimeout(() => {
          const transfersEl = document.getElementById('transfers')
          if (transfersEl) {
            transfersEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }, 80)
      }
    }

    window.addEventListener('salzburg-set-ski-destination', handleCustomSki)
    return () => {
      window.removeEventListener('salzburg-set-ski-destination', handleCustomSki)
    }
  }, [])

  const handleCancelSkiTransfer = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    setLockedSkiTransfer(null)
    setSelectedTo(null)

    if (searchParams.get('serviceType') === 'skitransfers') {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete('serviceType')
      nextParams.delete('serviceId')
      nextParams.delete('serviceTitle')
      nextParams.delete('servicePrice')
      nextParams.delete('toTitle')
      nextParams.delete('toAddress')
      setSearchParams(nextParams, { replace: true })
    }
  }

  useEffect(() => {
    if (!departure) {
      setReturnTrip(null)
      setIsReturnOpen(false)
    }
  }, [departure])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node
      const refs = [
        fromFieldRef,
        toFieldRef,
        durationFieldRef,
        departureFieldRef,
        returnFieldRef,
        passengerFieldRef,
        dayTripFieldRef,
      ]

      const clickedInside = refs.some(
        (ref) => ref.current && ref.current.contains(target),
      )

      if (!clickedInside) closeAllDropdowns()
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeAllDropdowns()
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscapeKey)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [])

  const handleSearch = () => {
    if (!isSearchReady || !selectedFrom || !departure) return

    const effectiveServiceType = lockedSkiTransfer ? 'skitransfers' : serviceType

    const params = new URLSearchParams({
      serviceType: effectiveServiceType,
      fromTitle: selectedFrom.title,
      fromAddress: selectedFrom.address,
      date: departure.date,
      time: departure.time,
      adults: adults.toString(),
      children: children.toString(),
    })

    if (selectedFrom.lat && selectedFrom.lng) {
      params.set('fromLat', selectedFrom.lat.toString())
      params.set('fromLng', selectedFrom.lng.toString())
    }

    if (isHourly) {
      params.set('skipStops', 'true')
      const effectiveDuration =
        activeDurationOption ||
        (durationHours ? getHourlyOption(durationHours) : null)
      if (effectiveDuration) {
        params.set('duration', effectiveDuration.hours.toString())
        params.set('distance', effectiveDuration.km.toString())
      }
    } else if (selectedTo) {
      params.set('toTitle', selectedTo.title)
      params.set('toAddress', selectedTo.address)
      if (selectedTo.lat && selectedTo.lng) {
        params.set('toLat', selectedTo.lat.toString())
        params.set('toLng', selectedTo.lng.toString())
      }
      if (returnTrip) {
        params.set('returnDate', returnTrip.date)
        params.set('returnTime', returnTrip.time)
      }
    }

    if (lockedSkiTransfer) {
      params.set('serviceType', 'skitransfers')
      if (lockedSkiTransfer.id) {
        params.set('serviceId', lockedSkiTransfer.id)
      }
      params.set('serviceTitle', lockedSkiTransfer.title)
      if (lockedSkiTransfer.price) {
        params.set('servicePrice', String(lockedSkiTransfer.price))
      }
    }

    navigate(`/booking?${params.toString()}`)
  }

  const handleFromChange = (location: LocationGeoData | null) => {
    setSelectedFrom(location)
  }

  const handleToChange = (location: LocationGeoData | null) => {
    setSelectedTo(location)
  }

  const handleSwapLocations = () => {
    if (!selectedFrom && !selectedTo) return
    const prevFrom = selectedFrom
    const prevTo = selectedTo
    setSelectedFrom(prevTo)
    setSelectedTo(prevFrom)
  }

  const handleDurationToggle = () => {
    const next = !isDurationOpen
    closeAllDropdowns()
    setIsDurationOpen(next)
  }

  const handleDepartureToggle = () => {
    const next = !isDepartureOpen
    closeAllDropdowns()
    setIsDepartureOpen(next)
  }

  const handleReturnToggle = () => {
    const next = !isReturnOpen
    closeAllDropdowns()
    setIsReturnOpen(next)
  }

  const handlePassengerToggle = () => {
    const next = !isPassengerOpen
    closeAllDropdowns()
    setIsPassengerOpen(next)
  }

  const handleDayTripToggle = () => {
    const next = !isDayTripOpen
    closeAllDropdowns()
    setIsDayTripOpen(next)
  }

  const passengerCount = adults + children

  const handleAdultsChange = (nextAdults: number) => {
    if (nextAdults + children > 16) {
      setIsPassengerOpen(false)
      setIsQuoteSent(false)
      setIsLargeGroupModalOpen(true)
      return
    }

    setAdults(nextAdults)
  }

  const handleChildrenChange = (nextChildren: number) => {
    if (adults + nextChildren > 16) {
      setIsPassengerOpen(false)
      setIsQuoteSent(false)
      setIsLargeGroupModalOpen(true)
      return
    }

    setChildren(nextChildren)
  }

  const closeLargeGroupModal = () => {
    setIsLargeGroupModalOpen(false)
    setIsQuoteSent(false)
    setIsQuoteCategoryOpen(false)
  }

  const handleQuoteSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsQuoteCategoryOpen(false)
    setIsQuoteSent(true)
  }

  useEffect(() => {
    if (!isLargeGroupModalOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleModalEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeLargeGroupModal()
      }
    }

    document.addEventListener('keydown', handleModalEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleModalEscape)
    }
  }, [isLargeGroupModalOpen])

  return (
    <div
      className="
        relative z-40 rounded-b-[20px] bg-white
        shadow-[0_4px_12px_rgba(0,0,0,0.08)]
        md:top-[-3vh]
        md:rounded-[clamp(10px,1.65vw,18px)]
        md:bg-[rgba(206,206,206,0.34)]
        md:p-[clamp(10px,2.45vh,22px)_clamp(6px,0.7vw,10px)]
        md:shadow-booking md:backdrop-blur-sm
      "
    >
      {serviceType === 'daytrips' ? (
        <div className="flex w-full">
          <div ref={dayTripFieldRef} className={`${blockBase} w-full`}>
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={isDayTripOpen}
              onClick={handleDayTripToggle}
              className="flex h-full w-full items-center justify-between gap-[8px] text-left"
            >
              <div className="flex min-w-0 flex-col justify-center">
                <p className={labelClass}>DAY TRIP</p>
                <p className={`${placeholderValueClass} mt-[clamp(4px,0.8vh,8px)]`}>
                  Where do you want to go?
                </p>
              </div>
              <FieldChevron open={isDayTripOpen} />
            </button>

            {isDayTripOpen && (
              <DayTripDropdown
                onSelect={(id) => {
                  setIsDayTripOpen(false)
                  navigate(`/day-trips?id=${id}`)
                }}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="grid w-full grid-cols-2 items-stretch gap-0 md:flex md:flex-nowrap md:gap-[clamp(5px,0.65vw,9px)]">
        {/* FROM */}
        <div
          ref={fromFieldRef}
          className={`${blockBase} col-span-2 md:flex-[1.38_1_0]`}
        >
          <HeroLocationInput
            label="FROM"
            placeholder="Search pickup address, hotel or airport"
            value={selectedFrom}
            onChange={handleFromChange}
            isRestrictedToSalzburg={isFromRestrictedToSalzburg}
            idPrefix="searchform"
          />
        </div>

        {/* DURATION / TO */}
        {isHourly ? (
          <div
            ref={durationFieldRef}
            className={`${blockBase} col-span-2 w-full md:flex-[0.95_1_0]`}
          >
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={isDurationOpen}
              onClick={handleDurationToggle}
              className="flex h-full w-full items-center justify-between gap-[8px] text-left"
            >
              <div className="flex min-w-0 flex-col justify-center">
                <p className={labelClass}>DURATION</p>
                <div className="mt-[clamp(4px,0.8vh,8px)] flex items-center gap-[clamp(4px,0.55vw,8px)]">
                  <p className={activeDurationOption ? valueClass : placeholderValueClass}>
                    {activeDurationOption ? activeDurationOption.label : 'Select hours'}
                  </p>
                  <img
                    src="/assets/images/icon-clock.png"
                    alt=""
                    className="h-[clamp(12px,1.45vw,20px)] w-[clamp(12px,1.45vw,20px)] shrink-0 object-contain"
                  />
                </div>
              </div>
              <FieldChevron open={isDurationOpen} />
            </button>

            {isDurationOpen && (
              <div
                className="absolute left-0 top-[calc(100%+12px)] z-[100] max-h-[260px] w-full min-w-[240px] sm:min-w-[280px] overflow-y-auto rounded-[15px] bg-white px-[14px] py-[8px] shadow-[0_8px_22px_rgba(0,0,0,0.25)]"
                role="listbox"
              >
                {HOURLY_DURATION_OPTIONS.map((opt) => {
                  const isSelected = activeDurationOption?.hours === opt.hours

                  return (
                    <button
                      key={opt.hours}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => {
                        setSelectedDurationOption(opt)
                        setDurationHours(opt.hours)
                        setIsDurationOpen(false)
                      }}
                      className={`flex min-h-[44px] w-full items-center border-b border-[#ececec] py-2 text-left font-poppins text-[15px] font-medium last:border-b-0 hover:bg-[#f8f8f8] ${
                        isSelected ? 'text-brand-orange font-semibold' : 'text-black'
                      }`}
                    >
                      <span>{opt.label}</span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        ) : (
          <div
            ref={toFieldRef}
            className={`${blockBase} col-span-2 md:flex-[1.42_1_0]`}
          >
            {!lockedSkiTransfer && (
              <div className="hidden md:block absolute left-[-10px] top-1/2 z-[60] -translate-x-1/2 -translate-y-1/2">
                <button
                  type="button"
                  onClick={handleSwapLocations}
                  title="Swap pickup and drop-off"
                  className="flex h-[clamp(34px,5.8vw,50px)] w-[clamp(34px,5.8vw,52px)] items-center justify-center rounded-full bg-[#efebe8] shadow-md transition-transform hover:scale-105 active:scale-95 cursor-pointer"
                >
                  <img
                    src="/assets/images/return-arrow.png"
                    alt="Swap"
                    className="h-auto w-auto object-contain"
                  />
                </button>
              </div>
            )}

            {lockedSkiTransfer ? (
              <div className="flex h-full w-full items-center justify-between gap-2">
                <div className="flex min-w-0 flex-col justify-center">
                  <div className="flex items-center gap-1.5">
                    <p className={labelClass}>TO</p>
                    <span className="rounded-full bg-[#B8DE79]/35 px-2 py-[2px] font-poppins text-[9px] font-semibold uppercase tracking-wider text-[#2D5A0F]">
                      Ski Transfer
                    </span>
                  </div>

                  <p className="mt-[clamp(2px,0.5vh,4px)] truncate font-poppins text-[15px] font-bold leading-tight text-black md:text-[17px]">
                    {lockedSkiTransfer.title}
                  </p>

                  <p className="truncate font-poppins text-[11px] font-normal text-[#666666] md:text-[12px]">
                    {lockedSkiTransfer.address}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleCancelSkiTransfer}
                  title="Cancel and pick another destination"
                  className="flex h-[32px] shrink-0 items-center gap-1 rounded-full border border-[#D0D0D0] bg-[#F7F7F7] px-3 font-poppins text-[11px] font-semibold text-[#444444] transition-all hover:border-red-400 hover:bg-red-50 hover:text-red-600 active:scale-95 cursor-pointer"
                >
                  <span className="text-[13px] font-bold leading-none">✕</span>
                  <span className="hidden sm:inline">Cancel</span>
                </button>
              </div>
            ) : (
              <HeroLocationInput
                label="TO"
                placeholder="Search drop-off destination, hotel or city"
                value={selectedTo}
                onChange={handleToChange}
                isRestrictedToSalzburg={isToRestrictedToSalzburg}
                idPrefix="searchform"
              />
            )}
          </div>
        )}

        {/* DEPARTURE */}
        <div
          ref={departureFieldRef}
          className={`${blockBase} relative col-span-1 border-r border-[#ECEFF1] md:flex-[1.05_1_0] md:border-0`}
        >
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={isDepartureOpen}
            onClick={handleDepartureToggle}
            className="flex h-full w-full items-center justify-between gap-[8px] text-left"
          >
            <div className="min-w-0">
              <p className={labelClass}>DEPARTURE</p>

              <div className="mt-[clamp(4px,0.8vh,8px)] flex items-center gap-[clamp(4px,0.65vw,9px)]">
                <p className={departure ? valueClass : placeholderValueClass}>
                  {formatDateLabel(departure?.date ?? '')}
                </p>
                <img
                  src="/assets/images/icon-calendar.png"
                  alt=""
                  className="h-[clamp(10px,1.25vw,17px)] w-[clamp(10px,1.25vw,17px)] object-contain"
                />
              </div>

              <div className="mt-[clamp(2px,0.35vh,5px)] flex items-center gap-[clamp(3px,0.45vw,7px)]">
                <span
                  className={`font-poppins text-[clamp(9px,1.05vw,14px)] font-medium leading-none tracking-[-0.03em] ${
                    departure ? 'text-black' : 'text-[#9A9A9A]'
                  }`}
                >
                  {formatTimeLabel(departure)}
                </span>
                <img
                  src="/assets/images/icon-clock.png"
                  alt=""
                  className="h-[clamp(8px,1vw,13px)] w-[clamp(8px,1vw,13px)] object-contain"
                />
              </div>
            </div>

            <FieldChevron open={isDepartureOpen} />
          </button>

          {isDepartureOpen && (
            <DateTimePicker
              value={departure}
              allowNow
              onCancel={() => setIsDepartureOpen(false)}
              onConfirm={(next) => {
                setDeparture(next)
                if (
                  returnTrip &&
                  (returnTrip.date < next.date ||
                    (returnTrip.date === next.date &&
                      returnTrip.time <= next.time))
                ) {
                  setReturnTrip(null)
                }
                setIsDepartureOpen(false)
              }}
            />
          )}
        </div>

        {/* RETURN — shown after departure; empty state is “Add a return” */}
        {showReturn && (
          <div
            ref={returnFieldRef}
            className={`${blockBase} relative hidden md:flex md:flex-[1.05_1_0]`}
          >
            <button
              type="button"
              aria-haspopup="dialog"
              aria-expanded={isReturnOpen}
              onClick={handleReturnToggle}
              className="flex h-full w-full items-center justify-between gap-[8px] text-left"
            >
              <div className="min-w-0">
                <p className={labelClass}>RETURN</p>

                {returnTrip ? (
                  <>
                    <div className="mt-[clamp(4px,0.8vh,8px)] flex items-center gap-[clamp(4px,0.65vw,9px)]">
                      <p className={valueClass}>
                        {formatDateLabel(returnTrip.date)}
                      </p>
                      <img
                        src="/assets/images/icon-calendar.png"
                        alt=""
                        className="h-[clamp(10px,1.25vw,17px)] w-[clamp(10px,1.25vw,17px)] object-contain"
                      />
                    </div>

                    <div className="mt-[clamp(2px,0.35vh,5px)] flex items-center gap-[clamp(3px,0.45vw,7px)]">
                      <span className="font-poppins text-[clamp(9px,1.05vw,14px)] font-medium leading-none tracking-[-0.03em] text-black">
                        {formatTimeLabel(returnTrip)}
                      </span>
                      <img
                        src="/assets/images/icon-clock.png"
                        alt=""
                        className="h-[clamp(8px,1vw,13px)] w-[clamp(8px,1vw,13px)] object-contain"
                      />
                    </div>
                  </>
                ) : (
                  <div className="mt-[clamp(8px,1.4vh,14px)] flex items-center gap-[clamp(6px,0.7vw,10px)]">
                    <p className={`${valueClass} text-[clamp(14px,1.35vw,18px)]`}>
                      Add a return
                    </p>
                    <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#F0F0F0] font-host text-[16px] font-light leading-none text-black">
                      +
                    </span>
                  </div>
                )}
              </div>

              <FieldChevron open={isReturnOpen} />
            </button>

            {isReturnOpen && (
              <DateTimePicker
                value={returnTrip}
                minDateTime={departure}
                onCancel={() => setIsReturnOpen(false)}
                onConfirm={(next) => {
                  setReturnTrip(next)
                  setIsReturnOpen(false)
                }}
              />
            )}
          </div>
        )}

        {/* PASSENGERS */}
        <div
          ref={passengerFieldRef}
          className={`
            ${blockBase}
            col-span-1 w-full border-b border-[#ECEFF1]
            md:w-[clamp(82px,9.2vw,131px)]
            md:flex-none md:border-0
            md:px-[clamp(7px,1vw,13px)]
          `}
        >
          {/* Mobile */}
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={isPassengerOpen}
            onClick={handlePassengerToggle}
            className="flex h-full w-full flex-col justify-center text-left md:hidden"
          >
            <p className={labelClass}>PASSENGERS</p>

            <div className="mt-[11px] flex items-center gap-[10px]">
              <img
                src="/assets/images/icon-adult.png"
                alt="Passengers"
                className="h-[24px] w-[24px] object-contain"
              />

              <span className="font-poppins text-[18px] font-semibold leading-none text-black">
                {passengerCount}
              </span>

              <FieldChevron open={isPassengerOpen} />
            </div>
          </button>

          {/* Desktop - unchanged layout */}
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={isPassengerOpen}
            onClick={handlePassengerToggle}
            className="hidden h-full w-full items-center justify-center gap-[clamp(4px,0.7vw,9px)] md:flex"
          >
            <div className="flex items-center gap-[clamp(2px,0.45vw,7px)]">
              <img
                src="/assets/images/icon-adult.png"
                alt="Passengers"
                className="h-[clamp(16px,2vw,26px)] w-[clamp(16px,2vw,26px)] object-contain"
              />

              <span className="font-poppins text-[clamp(11px,1.25vw,16px)] font-medium leading-none text-black">
                {passengerCount}
              </span>
            </div>

            <FieldChevron open={isPassengerOpen} />
          </button>

          {isPassengerOpen && (
            <Passenger
              adults={adults}
              children={children}
              onAdultsChange={handleAdultsChange}
              onChildrenChange={handleChildrenChange}
            />
          )}
        </div>

        {/* Search */}
        <button
          type="button"
          disabled={!isSearchReady}
          onClick={handleSearch}
          className="
            col-span-2 mx-[18px] my-[20px]
            flex h-[66px] w-[calc(100%_-_36px)]
            items-center justify-center
            rounded-[14px]
            bg-brand-orange
            font-poppins text-[19px] font-semibold
            leading-none text-white
            transition-colors
            hover:bg-[#e5631f]

            disabled:cursor-not-allowed
            disabled:bg-[#D0D0D0]
            disabled:text-[#8A8A8A]
            disabled:hover:bg-[#D0D0D0]

            md:mx-0 md:my-0
            md:h-[clamp(68px,12.2vh,96px)]
            md:w-[clamp(120px,13vw,185px)]
            md:shrink-0
            md:rounded-[clamp(10px,1.3vw,14px)]
            md:text-[clamp(14px,1.45vw,22px)]
          "
        >
          Search
        </button>
      </div>
      )}

      {isLargeGroupModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[99999] flex min-h-[100dvh] w-screen items-center justify-center overflow-y-auto bg-black/30 px-[16px] py-[20px]"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeLargeGroupModal()
              }
            }}
          >
            {!isQuoteSent ? (
              <form
                onSubmit={handleQuoteSubmit}
                role="dialog"
                aria-modal="true"
                aria-label="More than 16 passengers"
                className="my-auto w-full max-w-[653px] max-h-[calc(100dvh-40px)] overflow-y-auto rounded-[12px] bg-white px-[31px] pb-[18px] pt-[32px] shadow-[0_14px_40px_rgba(0,0,0,0.18)]"
                onMouseDown={(event) => event.stopPropagation()}
              >
                {/* HEADER */}
                <div className="flex items-start gap-[13px]">
                  <div className="flex h-[86px] w-[86px] shrink-0 items-center justify-center rounded-full bg-[#FFF78A]">
                    <img src='assets/images/modal-icon.png'/>
                  </div>

                  <div className="min-w-0 pt-[3px]">
                    <h2 className="font-poppins text-[24px] font-semibold leading-[1.15] tracking-[-0.02em] text-black">
                      More than 16 passengers?
                    </h2>
                    <p className="mt-[10px] max-w-[480px] font-poppins text-[15px] font-normal leading-[1.5] text-[#222222]">
                      Our standard vehicles can accommodate up to 16 passengers.
                      For larger groups, we’ll arrange a suitable vehicle or
                      multiple vehicles based on your requirements.
                    </p>
                  </div>
                </div>

                {/* FORM FIELDS */}
                <div className="mt-[38px] space-y-[12px]">
                  {/* PASSENGERS */}
                  <div className="grid gap-[8px] sm:grid-cols-[190px_minmax(0,1fr)] sm:items-center sm:gap-[31px]">
                    <div className="flex items-center gap-[10px]">
                      <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[#F3F3F3]">
                        <img
                          src="/assets/images/icon-adult.png"
                          alt=""
                          className="h-[16px] w-[16px] object-contain opacity-60"
                        />
                      </span>
                      <span className="font-poppins text-[13px] font-medium text-black">
                        Passengers
                      </span>
                    </div>
                    <div className="flex h-[56px] items-center rounded-[8px] border border-[#B9B9B9] px-[15px] font-poppins text-[15px] text-black">
                      17+ Passengers
                    </div>
                  </div>

                  {/* TRAVEL DATE */}
                  <div className="grid gap-[8px] sm:grid-cols-[190px_minmax(0,1fr)] sm:items-center sm:gap-[31px]">
                    <div className="flex items-center gap-[10px]">
                      <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[#F3F3F3]">
                        <img
                          src="/assets/images/icon-adult.png"
                          alt=""
                          className="h-[16px] w-[16px] object-contain opacity-60"
                        />
                      </span>
                      <span className="font-poppins text-[13px] font-medium text-black">
                        Travel Date
                      </span>
                    </div>
                    <input
                      type={quoteTravelDate ? 'date' : 'text'}
                      required
                      value={quoteTravelDate}
                      placeholder="Select Date"
                      onFocus={(event) => {
                        event.currentTarget.type = 'date'
                      }}
                      onBlur={(event) => {
                        if (!event.currentTarget.value) {
                          event.currentTarget.type = 'text'
                        }
                      }}
                      onChange={(event) => setQuoteTravelDate(event.target.value)}
                      className="h-[56px] w-full rounded-[8px] border border-[#B9B9B9] bg-white px-[15px] font-poppins text-[15px] text-black outline-none focus:border-black"
                    />
                  </div>

                  {/* CATEGORY */}
                  <div className="grid gap-[8px] sm:grid-cols-[190px_minmax(0,1fr)] sm:items-start sm:gap-[31px]">
                    <div className="flex items-center gap-[10px] sm:pt-[13px]">
                      <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[#F3F3F3]">
                        <img
                          src="/assets/images/icon-adult.png"
                          alt=""
                          className="h-[16px] w-[16px] object-contain opacity-60"
                        />
                      </span>
                      <span className="font-poppins text-[13px] font-medium text-black">
                        Category
                      </span>
                    </div>

                    <div className="relative">
                      <button
                        type="button"
                        aria-haspopup="listbox"
                        aria-expanded={isQuoteCategoryOpen}
                        onClick={() =>
                          setIsQuoteCategoryOpen((current) => !current)
                        }
                        className="flex h-[56px] w-full items-center justify-between rounded-[8px] border border-[#B9B9B9] bg-white px-[15px] text-left font-poppins text-[15px] text-black"
                      >
                        <span>{quoteCategory}</span>
                        <svg
                          viewBox="0 0 16 10"
                          fill="none"
                          className={`h-[10px] w-[16px] transition-transform ${
                            isQuoteCategoryOpen ? 'rotate-180' : ''
                          }`}
                          aria-hidden
                        >
                          <path
                            d="M1 1.5L8 8.5L15 1.5"
                            stroke="black"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>

                      {isQuoteCategoryOpen && (
                        <div className="absolute left-0 right-0 top-[65px] z-20 overflow-hidden rounded-[7px] border border-[#D2D2D2] bg-white shadow-[0_6px_16px_rgba(0,0,0,0.08)]">
                          {['Transfers', 'Hourly Trip', 'Day Trip'].map(
                            (category) => (
                              <button
                                key={category}
                                type="button"
                                role="option"
                                aria-selected={quoteCategory === category}
                                onClick={() => {
                                  setQuoteCategory(category)
                                  setIsQuoteCategoryOpen(false)
                                }}
                                className={`flex h-[44px] w-full items-center px-[12px] text-left font-poppins text-[15px] text-black hover:bg-[#BCE9EA] ${
                                  quoteCategory === category
                                    ? 'bg-[#BCE9EA]'
                                    : 'bg-white'
                                }`}
                              >
                                {category}
                              </button>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="h-[10px]" />

                  {/* PICKUP */}
                  <div className="grid gap-[8px] sm:grid-cols-[190px_minmax(0,1fr)] sm:items-center sm:gap-[31px]">
                    <div className="flex items-center gap-[10px]">
                      <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[#F3F3F3]">
                        <img
                          src="/assets/images/icon-adult.png"
                          alt=""
                          className="h-[16px] w-[16px] object-contain opacity-60"
                        />
                      </span>
                      <span className="font-poppins text-[13px] font-medium text-black">
                        Pickup Location
                      </span>
                    </div>
                    <input
                      type="text"
                      required
                      value={quotePickup}
                      onChange={(event) => setQuotePickup(event.target.value)}
                      placeholder="Enter Pickup Location"
                      className="h-[56px] w-full rounded-[8px] border border-[#B9B9B9] bg-white px-[15px] font-poppins text-[15px] text-black placeholder:text-[#151515] outline-none focus:border-black"
                    />
                  </div>

                  {/* DROP-OFF */}
                  <div className="grid gap-[8px] sm:grid-cols-[190px_minmax(0,1fr)] sm:items-center sm:gap-[31px]">
                    <div className="flex items-center gap-[10px]">
                      <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[#F3F3F3]">
                        <img
                          src="/assets/images/icon-adult.png"
                          alt=""
                          className="h-[16px] w-[16px] object-contain opacity-60"
                        />
                      </span>
                      <span className="font-poppins text-[13px] font-medium text-black">
                        Drop-off Location
                      </span>
                    </div>
                    <input
                      type="text"
                      required
                      value={quoteDropoff}
                      onChange={(event) => setQuoteDropoff(event.target.value)}
                      placeholder="Enter Drop Location"
                      className="h-[56px] w-full rounded-[8px] border border-[#B9B9B9] bg-white px-[15px] font-poppins text-[15px] text-black placeholder:text-[#151515] outline-none focus:border-black"
                    />
                  </div>

                  {/* ADDITIONAL REQUIREMENTS */}
                  <div className="grid gap-[8px] sm:grid-cols-[190px_minmax(0,1fr)] sm:items-start sm:gap-[31px]">
                    <div className="flex items-center gap-[10px] sm:pt-[11px]">
                      <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[#F3F3F3]">
                        <img
                          src="/assets/images/icon-adult.png"
                          alt=""
                          className="h-[16px] w-[16px] object-contain opacity-60"
                        />
                      </span>
                      <span className="font-poppins text-[13px] font-medium text-black">
                        Additional Requirements
                      </span>
                    </div>
                    <textarea
                      value={quoteRequirements}
                      onChange={(event) => setQuoteRequirements(event.target.value)}
                      placeholder="Tell us about your group, luggage, special requirements, etc."
                      className="h-[79px] w-full resize-none rounded-[8px] border border-[#B9B9B9] bg-white px-[14px] py-[10px] font-poppins text-[12px] leading-[1.4] text-black placeholder:text-[#969696] outline-none focus:border-black"
                    />
                  </div>
                </div>

                {/* BUTTONS */}
                <div className="mt-[24px] grid grid-cols-2 gap-[18px]">
                  <button
                    type="button"
                    onClick={closeLargeGroupModal}
                    className="h-[58px] rounded-[14px] bg-white font-poppins text-[15px] font-semibold text-black shadow-[0_4px_11px_rgba(0,0,0,0.14)] transition hover:bg-[#FAFAFA]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="h-[58px] rounded-[14px] bg-[#58D238] font-poppins text-[15px] font-semibold text-black transition hover:bg-[#50C832]"
                  >
                    Send Quote Request
                  </button>
                </div>
              </form>
            ) : (
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Quote Request Sent"
                className="w-full max-w-[435px] rounded-[10px] bg-white px-[28px] py-[27px] text-center shadow-[0_12px_34px_rgba(0,0,0,0.18)]"
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="relative mx-auto h-[72px] w-[90px]" aria-hidden>
                  <span className="absolute left-[8px] top-[15px] h-[6px] w-[6px] rounded-full bg-[#20E8A4]" />
                  <span className="absolute right-[12px] top-[3px] h-[7px] w-[7px] rounded-full bg-[#20E8A4]" />
                  <span className="absolute right-[4px] top-[39px] h-[5px] w-[5px] rounded-full bg-[#20E8A4]" />
                  <span className="absolute left-[7px] bottom-[10px] text-[12px] text-[#20E8A4]">
                    ★
                  </span>
                  <span className="absolute right-[16px] bottom-[2px] text-[10px] text-[#20E8A4]">
                    ★
                  </span>

                  <div className="absolute left-1/2 top-[14px] flex h-[44px] w-[44px] -translate-x-1/2 items-center justify-center rounded-full bg-[#20E8A4] shadow-[0_0_0_8px_rgba(32,232,164,0.08)]">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      className="h-[25px] w-[25px]"
                    >
                      <path
                        d="M5 12.5L9.4 17L19 7.5"
                        stroke="white"
                        strokeWidth="3.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>

                <h2 className="mt-[2px] font-poppins text-[18px] font-semibold leading-tight text-black">
                  Quote Request Sent
                </h2>
                <p className="mx-auto mt-[8px] max-w-[365px] font-poppins text-[11px] font-normal leading-[1.45] text-black">
                  Thank you for your purchase. Your order has been placed
                  successfully, and a confirmation email has been sent to your
                  registered email address.
                </p>
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}